/* eslint-disable dot-notation */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { doGet } from '../services/http';
import { errorHandler, getAsyncThunkMsg } from '../services/Utils';

/**
 * Async thunk to fetch order details
 * @param {Object} params - Query parameters
 * @param {string} params.order_id - Order ID to fetch
 * @returns {Promise<Object>} Order details
 */
export const fetchOrderDetails = createAsyncThunk(
  'orderDetails/fetchOrderDetails',
  async (params, { rejectWithValue }) => {
    try {
      console.log('fetchOrderDetails params:', params);

      const response = await doGet(`orders/details/?order_id=${params?.order_id}`);
      return response.data;
    } catch (err) {
      console.error('fetchOrderDetails error:', err);

      errorHandler(err);

      // Always send a serializable, clean message
      return rejectWithValue(getAsyncThunkMsg(err));
    }
  }
);

/**
 * Redux slice for single order details state
 * Manages order detail view with refresh capability
 */
const OrderDetailsSlice = createSlice({
  name: 'orderDetails',
  initialState: {
    order: null,
    status: 'idle',
    error: null,
    refreshing: false,
    loadingMore: false,
  },
  reducers: {
    /** Enables pull-to-refresh control */
    enableRefreshControl(state, action) {
      if (!action.payload?.retry) {
        state.refreshing = true;
      }
    },
    /** Disables pull-to-refresh control */
    disableRefreshControl(state, action) {
      state.refreshing = false;
    },
    /** Resets order details state to initial values */
    resetData(state, action) {
      return OrderDetailsSlice.getInitialState();
    },
    /** Updates order with partial data */
    updateOrder(state, action) {
      if (state.order) {
        state.order = { ...state.order, ...action.payload };
      }
    },
    /** Updates order feedback/rating info */
    updateOrderDetailsFeedback(state, action) {
      if (state.order) {
        state.order['has_review'] = action.payload?.has_review;
        state.order['order_rating'] = action.payload?.order_rating;
      }
    },
    /** Updates order cancellation status */
    updateOrderDetailsCancellation(state, action) {
      if (state.order) {
        state.order['order_status'] = action.payload?.order_status;
        state.order['order_status_name'] = action.payload?.order_status_name;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrderDetails.pending, (state, action) => {
      if (!state.refreshing) {
        state.status = 'loading';
      }
    })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.order = action.payload;
        state.refreshing = false;
        // console.log(action.payload);
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action?.payload;
        state.refreshing = false;
      });
  },
});

export const {
  enableRefreshControl,
  disableRefreshControl,
  resetData,
  updateOrder,
  updateOrderDetailsFeedback,
  updateOrderDetailsCancellation,
} = OrderDetailsSlice.actions;
export default OrderDetailsSlice.reducer;
